import React, { useEffect, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import {
    TextField,
    InputLabel,
    FormControl,
    Typography,
    IconButton,
    Select,
    MenuItem,
    Tooltip,
    FormHelperText,
    Radio,
    RadioGroup,
    FormControlLabel,
    Checkbox
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import TagsInput from '../../../collums-components/form/TagsInput';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import HelpIcon from '@material-ui/icons/Info';

const quantityOptions = new Array(100).fill(0).map((el, index) => index + 1);
const repeatEveryOptions = new Array(52).fill(0).map((el, index) => index + 1);
const expiryNumberOptions = new Array(36).fill(0).map((el, index) => index + 1);

function MembershipFormGeneral({
    classes,
    validationMessages,
    register,
    control,
    watch,
    setValue,
    services,
    getServices,
    categories,
    getMembershipCategories,
    setFilterServices,
    isFromOrg,
    allClinics,
    errors,
    selectedMembership
}) {
    const getExpiryUnit = months => {
        if (!months) return 'months';
        return months >= 12 && months % 12 === 0 ? 'years' : 'months';
    };
    const getExpiryNumber = months => {
        if (!months) return '';
        return months >= 12 && months % 12 === 0 ? months / 12 : months;
    };
    const initialExpiry = selectedMembership?.expiryAfterMonths || null;
    const [expiryUnit, setExpiryUnit] = useState(() => getExpiryUnit(initialExpiry));
    const [expiryNumber, setExpiryNumber] = useState(() => getExpiryNumber(initialExpiry));
    useEffect(() => {
        if (initialExpiry) {
            setValue('expiryAfterMonths', initialExpiry);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'services'
    });

    return (
        <div className={classes.formContent}>
            <div className={classes.formItem}>
                <InputLabel shrink>Name *</InputLabel>
                <TextField
                    style={{ width: '100%', marginTop: 10 }}
                    inputRef={register({ required: validationMessages.required })}
                    name="name"
                    variant="outlined"
                    inputProps={{ readOnly: !isFromOrg && allClinics.length > 1 }}
                    error={!!errors?.name}
                    helperText={errors?.name?.message}
                />
            </div>
            <div className={classes.formItem}>
                <InputLabel shrink>Description</InputLabel>
                <TextField
                    style={{ width: '100%', marginTop: 10 }}
                    inputRef={register}
                    name="description"
                    variant="outlined"
                    multiline
                    rowsMax={3}
                    placeholder="Leave blank to auto-populate with list of membership services"
                    className={classes.descriptionField}
                    inputProps={{ readOnly: !isFromOrg && allClinics.length > 1 }}
                />
            </div>

            <Controller
                render={({ value, onChange }) => (
                    <Autocomplete
                        className={`${classes.formItem} ${classes.autocomplete}`}
                        onClose={async () => {
                            await getMembershipCategories({ name: '' });
                        }}
                        options={categories}
                        getOptionLabel={option => option.name || ''}
                        getOptionSelected={(option, category) => option === category}
                        onChange={(e, category) => {
                            onChange(category);
                        }}
                        clearOnEscape
                        value={value}
                        onInputChange={async (e, val) => {
                            await getMembershipCategories({ name: val });
                        }}
                        renderInput={params => (
                            <FormControl style={{ width: '100%' }} error={!!errors?.category}>
                                <InputLabel shrink>Category *</InputLabel>
                                <TextField
                                    style={{ marginTop: 10 }}
                                    {...params}
                                    variant="outlined"
                                    error={!!errors?.category}
                                />
                                {errors?.category && <FormHelperText error>{errors.category.message}</FormHelperText>}
                            </FormControl>
                        )}
                        renderOption={option => {
                            return <p className={classes.autocompleteOptions}>{`${option.name}`}</p>;
                        }}
                    />
                )}
                name={'category'}
                control={control}
                rules={{ required: validationMessages.required }}
            />

            <div className={classes.formContentRow} style={{ marginTop: 20 }}>
                <Typography variant="h4" className={classes.sectionName}>
                    Services included in membership
                    <IconButton
                        onClick={() => {
                            append({
                                service: null,
                                quantity: 1,
                                serviceType: 'repeating',
                                repeatEvery: 1,
                                availableFrom: 'immediately',
                                rollover: false
                            });
                        }}
                        className={classes.addCircle}
                        disabled={!isFromOrg && allClinics.length > 1}
                    >
                        <AddCircleIcon style={{ fontSize: 30, color: '#3083EC' }} />
                    </IconButton>
                </Typography>
            </div>

            {fields.map((field, index) => {
                return (
                    <div key={field.id} className={classes.serviceRow}>
                        <div style={{ flex: 1 }}>
                            {/* Service selection */}
                            <Controller
                                render={({ value, onChange }) => (
                                    <Autocomplete
                                        disabled={!isFromOrg && allClinics.length > 1}
                                        className={classes.autocomplete}
                                        style={{ marginBottom: 16, width: '48%' }}
                                        onClose={async () => {
                                            setFilterServices({ value: '' });
                                            await getServices({ value: '' });
                                        }}
                                        options={services || []}
                                        getOptionLabel={option => option.name || ''}
                                        getOptionSelected={(option, service) => option.id === service?.id}
                                        onChange={(e, service) => {
                                            onChange(service);
                                        }}
                                        clearOnEscape
                                        value={value}
                                        onInputChange={async (e, val) => {
                                            setFilterServices({ value: val });
                                        }}
                                        renderInput={params => (
                                            <FormControl style={{ width: '100%' }}>
                                                <InputLabel shrink>Service *</InputLabel>
                                                <TextField style={{ marginTop: 10 }} {...params} variant="outlined" />
                                            </FormControl>
                                        )}
                                        renderOption={option => {
                                            return <p className={classes.autocompleteOptions}>{`${option.name}`}</p>;
                                        }}
                                    />
                                )}
                                name={`services[${index}].service`}
                                control={control}
                                defaultValue={field.service}
                                rules={{ required: validationMessages.required }}
                            />

                            {/* Service Type Radio - inline */}
                            <div className={classes.inlineRow}>
                                <Typography style={{ marginRight: 16 }}>Type of service:</Typography>
                                <Controller
                                    render={({ value, onChange }) => (
                                        <RadioGroup
                                            row
                                            value={value || 'repeating'}
                                            onChange={e => onChange(e.target.value)}
                                        >
                                            <FormControlLabel
                                                value="one-off"
                                                control={
                                                    <Radio
                                                        size="small"
                                                        disabled={!isFromOrg && allClinics.length > 1}
                                                    />
                                                }
                                                label="Non-repeating"
                                            />
                                            <FormControlLabel
                                                value="repeating"
                                                control={
                                                    <Radio
                                                        size="small"
                                                        disabled={!isFromOrg && allClinics.length > 1}
                                                    />
                                                }
                                                label="Repeating"
                                            />
                                        </RadioGroup>
                                    )}
                                    name={`services[${index}].serviceType`}
                                    control={control}
                                    defaultValue={field.serviceType || 'repeating'}
                                />
                            </div>

                            {/* Allow X service(s) [every Y months] - inline */}
                            <div className={classes.inlineRow}>
                                <Typography style={{ minWidth: 70 }}>Allow:</Typography>
                                <Controller
                                    render={({ value, onChange }) => (
                                        <Select
                                            disabled={!isFromOrg && allClinics.length > 1}
                                            value={value || 1}
                                            onChange={e => onChange(e.target.value)}
                                            variant="outlined"
                                            style={{ minWidth: 80 }}
                                        >
                                            {quantityOptions.map(n => (
                                                <MenuItem key={n} value={n}>
                                                    {n}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                    name={`services[${index}].quantity`}
                                    control={control}
                                    defaultValue={field.quantity || 1}
                                    rules={{ required: validationMessages.required }}
                                />
                                <Typography style={{ whiteSpace: 'nowrap' }}>service(s)</Typography>

                                {/* Repeat fields - only show for repeating type */}
                                {watch(`services[${index}].serviceType`) === 'repeating' && (
                                    <>
                                        <Typography>every</Typography>
                                        <Controller
                                            render={({ value, onChange }) => (
                                                <Select
                                                    disabled={!isFromOrg && allClinics.length > 1}
                                                    value={value || 1}
                                                    onChange={e => onChange(e.target.value)}
                                                    variant="outlined"
                                                    style={{ minWidth: 80 }}
                                                >
                                                    {repeatEveryOptions.map(n => (
                                                        <MenuItem key={n} value={n}>
                                                            {n}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                            name={`services[${index}].repeatEvery`}
                                            control={control}
                                            defaultValue={field.repeatEvery || 1}
                                        />
                                        <Typography>months</Typography>
                                    </>
                                )}
                            </div>

                            {/* Available from */}
                            <div className={classes.inlineRow} style={{ marginTop: 8 }}>
                                <Typography style={{ minWidth: 70, whiteSpace: 'nowrap' }}>Available:</Typography>
                                <Controller
                                    render={({ value, onChange }) => (
                                        <Select
                                            disabled={!isFromOrg && allClinics.length > 1}
                                            value={value || 'immediately'}
                                            onChange={e => onChange(e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            style={{ minWidth: 160 }}
                                        >
                                            <MenuItem value="immediately">Immediately</MenuItem>
                                            <MenuItem value="after-payment">After payment</MenuItem>
                                        </Select>
                                    )}
                                    name={`services[${index}].availableFrom`}
                                    control={control}
                                    defaultValue={field.availableFrom || 'immediately'}
                                />
                                <Tooltip
                                    placement="right"
                                    title={
                                        <Typography style={{ fontSize: '14px', display: 'grid' }}>
                                            <b>Immediately:</b> Membership will be loaded onto client card as soon as
                                            Direct Debit request is sent (not when it has been completed)
                                            <br />
                                            <b>After payment:</b> Membership will be loaded onto client card once the
                                            first Direct Debit payment has been received in full.
                                        </Typography>
                                    }
                                    aria-label="help"
                                >
                                    <HelpIcon className={classes.helpIcon} />
                                </Tooltip>
                            </div>

                            {/* Rollover - only for repeating */}
                            {watch(`services[${index}].serviceType`) === 'repeating' && (
                                <div className={classes.inlineRow}>
                                    <Controller
                                        render={({ value, onChange }) => (
                                            <FormControlLabel
                                                disabled={!isFromOrg && allClinics.length > 1}
                                                control={
                                                    <Checkbox
                                                        checked={!!value}
                                                        onChange={e => onChange(e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label="Allow roll over of unused services"
                                            />
                                        )}
                                        name={`services[${index}].rollover`}
                                        control={control}
                                        defaultValue={field.rollover || false}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Delete button */}
                        <IconButton
                            onClick={() => remove(index)}
                            disabled={!isFromOrg && allClinics.length > 1}
                            style={{ alignSelf: 'flex-start', marginTop: 20 }}
                        >
                            <DeleteOutlineOutlinedIcon className={classes.deleteIcon} />
                        </IconButton>
                    </div>
                );
            })}

            {fields.length === 0 && (
                <Typography color="error" style={{ marginBottom: 16 }}>
                    At least one service is required. Click the + button above to add a service.
                </Typography>
            )}

            <div className={classes.formItem} style={{ marginTop: 20 }}>
                <InputLabel shrink>Membership expires after</InputLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                    <Select
                        disabled={!isFromOrg && allClinics.length > 1}
                        value={expiryNumber}
                        onChange={e => {
                            const num = e.target.value;
                            setExpiryNumber(num);
                            if (num === '') {
                                setValue('expiryAfterMonths', null);
                            } else {
                                const months = expiryUnit === 'years' ? num * 12 : num;
                                setValue('expiryAfterMonths', months);
                            }
                        }}
                        variant="outlined"
                        displayEmpty
                        style={{ minWidth: 80 }}
                    >
                        <MenuItem value="">None</MenuItem>
                        {expiryNumberOptions.map(n => (
                            <MenuItem key={n} value={n}>
                                {n}
                            </MenuItem>
                        ))}
                    </Select>
                    <Select
                        disabled={(!isFromOrg && allClinics.length > 1) || !expiryNumber}
                        value={expiryUnit}
                        onChange={e => {
                            const unit = e.target.value;
                            setExpiryUnit(unit);
                            if (expiryNumber) {
                                const months = unit === 'years' ? expiryNumber * 12 : expiryNumber;
                                setValue('expiryAfterMonths', months);
                            }
                        }}
                        variant="outlined"
                        style={{ minWidth: 100 }}
                    >
                        <MenuItem value="months">Month(s)</MenuItem>
                        <MenuItem value="years">Year(s)</MenuItem>
                    </Select>
                </div>
                <Controller
                    name="expiryAfterMonths"
                    control={control}
                    defaultValue={initialExpiry}
                    render={() => <input type="hidden" />}
                />
            </div>

            <div className={classes.formContentRow} style={{ marginTop: 20 }}>
                <TagsInput disabled={!isFromOrg && allClinics.length > 1} name="tags" control={control} />
            </div>
        </div>
    );
}

export default MembershipFormGeneral;
